import { useState } from 'react';
import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API_BASE_URL, ENVIRONMENT } from '@/config/env';
import type { SubscriptionPlanSummary } from '@/services/types';

/**
 * M9 — the confirmation that stands between a row's Sync button and a write to
 * whichever Stripe account the deployed environment's `STRIPE_SECRET_KEY`
 * points at.
 *
 * **It ships degraded, deliberately, and this is the whole of the difference.**
 * M9's showcase row prints the amounts Stripe has on file against the ones the
 * catalog holds — "The annual price on file is $299.99; this catalog row says
 * $287.90". Nothing serialises those amounts: `SerializedPlan`
 * (`api/src/features/admin/services/subscription-management.ts:30-48`) has no
 * Stripe amount and neither has `SubscriptionPlanSummary`, and the
 * `stripe-preview` endpoint that would have supplied them was **declined** by
 * the `api` section. So the annual risk is stated conditionally rather than
 * numerically, and the confirm button is **`destructive` permanently**: with no
 * server answer about what will be created, "nothing new can be made" is never
 * knowable, which makes M9's calm `primary` variant unreachable. It is not
 * written here, and no test asserts it: a green assertion about a state the
 * product cannot enter is how an unreachable branch earns a coverage waiver.
 *
 * Everything else in M9 renders from data admin already holds, on every row, in
 * every environment.
 */

interface SyncPlanDialogProps {
  /** The row this dialog belongs to. One dialog per row, opened by its own button. */
  plan: SubscriptionPlanSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  /**
   * The row's own Sync button. It is a real `DialogTrigger` rather than a
   * state-only opener for two reasons M9 asks for by name: Radix puts
   * `aria-haspopup="dialog"` on a trigger itself — the one thing that tells a
   * screen-reader user the button opens a dialog rather than firing the sync —
   * and `DialogContentModal` returns focus on close to `triggerRef`, which is
   * how "focus returns to the row's own Sync button, so the operator can see
   * which row they did not sync" is true in code. With no trigger that ref is
   * null and the close hands focus to `document.body`.
   */
  children: ReactNode;
}

type StepTone = 'create' | 'keep' | null;

interface SyncStep {
  tone: StepTone;
  body: ReactNode;
}

function usd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function buildAlerts(plan: SubscriptionPlanSummary): string[] {
  const alerts: string[] = [];

  if (plan.monthlyPrice === 0 && plan.annualPrice === 0) {
    alerts.push(
      'Both amounts on this plan are $0.00. Syncing it creates a Stripe product carrying two $0.00 recurring prices.',
    );
  }

  // Stated conditionally because the amount on file is not on the wire. The
  // risk exists only where an annual price id is already recorded: a row with
  // none has nothing for a second price to sit beside.
  if (plan.stripePriceIdAnnual !== null) {
    alerts.push(
      `A Stripe price cannot be edited. If the amount on file differs from ${usd(plan.annualPrice)}, this adds a second annual price and the existing one stays on every subscription already using it.`,
    );
  }

  return alerts;
}

function buildIntervalStep(
  interval: 'Monthly' | 'Annual',
  priceId: string | null,
  amount: number,
): SyncStep {
  if (priceId === null) {
    return {
      tone: 'create',
      body: (
        <>
          {interval}: no price id is on file — <b>creates a price</b> at {usd(amount)}.
        </>
      ),
    };
  }

  return {
    tone: 'create',
    body: (
      <>
        {interval}: <code>{priceId}</code> is on file. If its amount differs from{' '}
        {usd(amount)}, this <b>creates a new {interval.toLowerCase()} price</b>.
      </>
    ),
  };
}

function buildSteps(plan: SubscriptionPlanSummary): SyncStep[] {
  if (plan.stripeProductId === null) {
    return [
      {
        tone: null,
        body: (
          <>
            Look for a Stripe product tagged <code>metadata.planId = {plan.id}</code>.
          </>
        ),
      },
      {
        tone: 'create',
        body: (
          <>
            <b>If one exists, adopt it. If not, create a new product</b> named “
            {plan.name}”. The result is not known until the lookup runs.
          </>
        ),
      },
      {
        tone: 'create',
        body: (
          <>
            <b>Create two prices</b> — {usd(plan.monthlyPrice)}/month and{' '}
            {usd(plan.annualPrice)}/year — because no price ids are on file.
          </>
        ),
      },
    ];
  }

  return [
    {
      tone: 'keep',
      body: (
        <>
          <b>Update</b> the existing product <code>{plan.stripeProductId}</code> — name,
          description, active flag, metadata. <b>No product is created.</b>
        </>
      ),
    },
    buildIntervalStep('Monthly', plan.stripePriceIdMonthly, plan.monthlyPrice),
    buildIntervalStep('Annual', plan.stripePriceIdAnnual, plan.annualPrice),
  ];
}

function buildWrites(plan: SubscriptionPlanSummary): ReactNode[] {
  const mayCreate = plan.stripeProductId === null;

  return [
    mayCreate ? (
      <>
        <code>stripe_product_id</code> — the adopted or created product id
      </>
    ) : (
      <>
        <code>stripe_product_id</code> — unchanged (<code>{plan.stripeProductId}</code>)
      </>
    ),
    <>
      <code>stripe_price_id_monthly</code> —{' '}
      {mayCreate
        ? 'the new price id'
        : 'unchanged, unless the amount on file differs — then the new price id'}
    </>,
    <>
      <code>stripe_price_id_annual</code> —{' '}
      {mayCreate
        ? 'the new price id'
        : 'unchanged, unless the amount on file differs — then the new price id'}
    </>,
    <>
      <code>stripe_sync_status</code> — <code>pending</code> now, then <code>synced</code>,
      or <code>failed</code> if Stripe rejects it
    </>,
    <>
      <code>last_synced_at</code> — this moment, on success only
    </>,
  ];
}

const TONE_CLASS: Record<'create' | 'keep', string> = {
  create: 'bg-destructive/10 text-destructive',
  keep: 'bg-muted text-muted-foreground',
};

/**
 * The body, and it is a separate component on purpose: it is a child of
 * `DialogContent`, so Radix mounts it when the dialog opens and unmounts it
 * when the dialog closes. The typed tier therefore starts empty on every open,
 * with no effect to reset it.
 */
function SyncPlanDialogBody({
  plan,
  onConfirm,
}: {
  plan: SubscriptionPlanSummary;
  onConfirm: () => void;
}) {
  const [token, setToken] = useState('');

  const matches = token.trim().toUpperCase() === plan.tier;
  const alerts = buildAlerts(plan);
  const steps = buildSteps(plan);
  const writes = buildWrites(plan);

  return (
    <>
      <DialogHeader>
        <DialogTitle>Sync {plan.name} to Stripe?</DialogTitle>
        <DialogDescription>
          {plan.stripeProductId === null
            ? 'No Stripe product is recorded for this plan. This may create one.'
            : 'A Stripe product is recorded for this plan. This updates it in place.'}
        </DialogDescription>
      </DialogHeader>

      <div className="rounded-xl border border-border/70 px-3 py-2 text-sm">
        <b className="mr-2 text-xs uppercase tracking-wide text-muted-foreground">Target</b>
        <span>
          <code>{ENVIRONMENT}</code> — {API_BASE_URL}, and the Stripe account that
          environment’s <code>STRIPE_SECRET_KEY</code> points at.
        </span>
      </div>

      {alerts.map((alert) => (
        <div
          key={alert}
          role="alert"
          className="flex items-start gap-2 rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{alert}</span>
        </div>
      ))}

      <ol className="list-decimal space-y-2 pl-5 text-sm">
        {steps.map((step, index) => (
          <li key={index}>
            {step.tone === null ? null : (
              <span
                className={`mr-2 rounded px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${TONE_CLASS[step.tone]}`}
              >
                {step.tone}
              </span>
            )}
            {step.body}
          </li>
        ))}
      </ol>

      <div className="rounded-xl border border-dashed border-border/70 px-3 py-2 text-sm">
        <b>
          Then writes back to <code>subscription_plans</code>:
        </b>
        <ul className="mt-1 list-disc space-y-1 pl-5">
          {writes.map((write, index) => (
            <li key={index}>{write}</li>
          ))}
        </ul>
      </div>

      <div className="space-y-1">
        <label className="text-sm" htmlFor="sync-plan-token">
          Type <code>{plan.tier}</code> to enable the button
        </label>
        <Input
          id="sync-plan-token"
          value={token}
          autoComplete="off"
          spellCheck={false}
          aria-describedby="sync-plan-hint"
          onChange={(event) => setToken(event.target.value)}
          onKeyDown={(event) => {
            // Enter confirms only when the field already matches — otherwise it
            // is the same stray key press the field exists to absorb.
            if (event.key === 'Enter' && matches) {
              event.preventDefault();
              onConfirm();
            }
          }}
        />
        <p className="text-xs text-muted-foreground" id="sync-plan-hint">
          A Stripe price object cannot be deleted, only archived. Type the tier to confirm
          you intend a write to the {ENVIRONMENT} Stripe account.
        </p>
      </div>

      <DialogFooter>
        {/* Cancel, the close button, Esc and the overlay are one path: Radix
            resolves all four through `onOpenChange(false)`, and none of them
            can reach the mutation. */}
        <DialogClose asChild>
          <Button variant="outline">Cancel</Button>
        </DialogClose>
        <Button variant="destructive" disabled={!matches} onClick={onConfirm}>
          Sync {plan.name}
        </Button>
      </DialogFooter>
    </>
  );
}

export function SyncPlanDialog({
  plan,
  open,
  onOpenChange,
  onConfirm,
  children,
}: SyncPlanDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto">
        {/* No `onOpenAutoFocus` override: the tier field is the first tabbable
            node inside the content, so Radix's own autofocus lands on it rather
            than on a button — which is M9's requirement, met by DOM order. */}
        <SyncPlanDialogBody plan={plan} onConfirm={onConfirm} />
      </DialogContent>
    </Dialog>
  );
}
